import React from 'react';

import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

type MultiDiscountSelectProps = {
  id: string;
  value: Array<string>;
  promoDiscounts: definitions['InternalPromoWithDiscounts']['promo_discounts'];
  multiSelectType: 'promo_code_discount_ids';
};

export const buildPromoKeyDisplay = (
  loyaltyPointsBonusConfig: definitions['InternalPromoWithDiscounts']['promo_discounts'][0]['loyalty_points_bonus_config'],
): string => {
  return loyaltyPointsBonusConfig?.loyaltyPointsBonusConfigV1?.map((config) => config.loyaltyPromoKey).join(', ');
};

export const renderMultiDiscountValue = (
  selected: Array<string>,
  promoDiscounts: definitions['InternalPromoWithDiscounts']['promo_discounts'],
): string => {
  if (selected.length <= 3) {
    const match: definitions['InternalPromoWithDiscounts']['promo_discounts'] = promoDiscounts.reduce((acc, opt) => {
      if (selected.includes(opt.id_promo_code_discount)) {
        acc.push(opt);
      }
      return acc;
    }, []);
    const loyaltyTags = match
      .map((opt) => buildPromoKeyDisplay(opt.loyalty_points_bonus_config))
      .flat()
      .join(', ');
    return match.map((opt) => opt.region + ' - ' + opt.discount_value + ' - ' + loyaltyTags).join(', ');
  } else if (selected.length == 0) {
    return '';
  } else {
    return `${selected.length} Discounts Selected`;
  }
};

function MultiDiscountSelect({ id, value, promoDiscounts, multiSelectType }: MultiDiscountSelectProps) {
  const apiRef = useGridApiContext();

  const handleValueChange = (event: SelectChangeEvent<Array<string>>) => {
    const newValue = event.target.value;
    apiRef.current.setEditCellValue({ id, field: multiSelectType, value: newValue });
  };

  const discountOptions = promoDiscounts.map((opt) => ({
    isChecked: value.includes(opt.id_promo_code_discount),
    label: `${opt.region} - ${opt.discount_value} - ${buildPromoKeyDisplay(opt.loyalty_points_bonus_config)}`,
    value: opt.id_promo_code_discount,
  }));

  return (
    <FormControl fullWidth>
      <InputLabel>Select Discounts</InputLabel>
      <Select
        labelId={multiSelectType}
        id={multiSelectType}
        multiple
        value={value}
        label="Select Discounts"
        onChange={handleValueChange}
        renderValue={(selected) => renderMultiDiscountValue(selected, promoDiscounts)}
        fullWidth
        sx={{
          minHeight: 56,
          '& .MuiSelect-select': {
            minHeight: 56,
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {discountOptions.map((opt, i) => (
          <MenuItem key={`${i}_${opt.value}`} value={opt.value} sx={{ minWidth: 200 }}>
            <Checkbox checked={opt.isChecked} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default MultiDiscountSelect;
